import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasStore } from "./store/canvas-store";
import { compareCanvasObjectsForRender } from "@refinement-canvas/domain";
import { StickyObject } from "./objects/sticky-object";
import { SectionObject } from "./objects/section-object";
import { ShapeObject } from "./objects/shape-object";
import { TableObject } from "./objects/table-object";
import { TextObject } from "./objects/text-object";
import { ImageObject } from "./objects/image-object";
import { VideoObject } from "./objects/video-object";
import { LinkCardObject } from "./objects/link-card-object";
import { PenStrokeObject } from "./objects/pen-stroke-object";
import { StampObject } from "./objects/stamp-object";
import { ConnectorObject } from "./objects/connector-object";
import { aabbIntersects, viewportInStage } from "../../lib/culling";

// オブジェクト全件を画面に描画するレイヤ。
// zOrder の昇順で並べ、後で描画されるもの（=zOrder が大きいもの）が上に重なる。
// 同 zOrder の場合は id で安定ソートする。各オブジェクトの root 要素には
// `style.zIndex = zOrder` を付与し、CSS の重なり順も zOrder と一致させる
// （オブジェクト自身の position: absolute と組み合わせて機能する）。
//
// ビューポートカリング:
// 表示中の screen 矩形をステージ座標に逆変換し、各オブジェクトの AABB と
// 交差するもののみを描画する。rotation を持つ object のはみ出し対策に
// 256px のパディングを加える（最大 30%程度の回転までは十分な余白）。
// 5,000 オブジェクト hard limit を踏まえても線形フィルタで毎フレーム十分高速。
//
// ペンストロークも通常オブジェクトとして描画し、zOrder に従って重なり順を揃える。
const CULLING_PAD_PX = 256;

export function CanvasObjectsLayer(): JSX.Element {
  const objects = useCanvasDocStore((s) => s.objects);
  const view = useCanvasStore((s) => s.view);
  // ウィンドウサイズを reactive な state として保持し、resize で再カリングする。
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  }));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = (): void => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const ordered = useMemo(() => {
    return (
      Object.values(objects)
        // コメントスレッドは専用のオーバーレイ層（CommentLayer）でビューポート固定サイズで描画する。
        // 通常オブジェクト層では描画せず、選択・右クリック・整列などの汎用処理からも外す。
        .filter((o) => o.type !== "comment-thread")
        .slice()
        // セクション帯 → 通常帯 → zOrder → id の共有比較関数で並べる。CSS の重なり順は
        // 各オブジェクトの zIndex（セクションはバンドオフセットで負値）が担うため、
        // ここでの並びは同 zIndex 時のフォールバック（DOM 順）とサムネイル等との整合を担う。
        .sort(compareCanvasObjectsForRender)
    );
  }, [objects]);

  // 選択中オブジェクトはカリング対象から外して必ず描画する。
  // CanvasSelectionOverlay が targetRef で対象 DOM を握っているため、選択中に
  // カリングでアンマウントされるとリサイズ/回転プレビューが効かなくなる。
  const selectedIds = useCanvasStore((s) => s.selectedObjectIds);

  const visible = useMemo(() => {
    if (viewportSize.width === 0 || viewportSize.height === 0) return ordered;
    const viewport = viewportInStage(view, viewportSize.width, viewportSize.height, CULLING_PAD_PX);
    const selectedSet = new Set(selectedIds);
    return ordered.filter(
      (obj) =>
        selectedSet.has(obj.id) ||
        // コネクタは保存値 x/y/w/h が接続先移動後に古くなりうるため（描画は端点から都度計算）、
        // カリングから除外して常に描画する。接続先がビューポート外でも線が途切れないようにする。
        obj.type === "connector" ||
        aabbIntersects(viewport, { x: obj.x, y: obj.y, w: obj.w, h: obj.h })
    );
  }, [ordered, view, viewportSize, selectedIds]);

  const openContextMenu = useCanvasStore((s) => s.openContextMenu);
  const setSelectedObjectIds = useCanvasStore((s) => s.setSelectedObjectIds);
  // 参照専用（埋め込み）では右クリックでの選択・コンテキストメニューを行わない。
  // これをしないと、選択経由で CanvasConnectorOverlay が現れ、readOnly でもコネクタ作成という
  // 編集操作が残ってしまう。readOnly では preventDefault もせず、ブラウザ標準メニューに委ねる。
  const readOnly = useCanvasStore((s) => s.readOnly);

  // 右クリック時に対象オブジェクトを選択しつつコンテキストメニューを開く。
  // 編集中の contentEditable 要素では既定のテキストメニューを優先するため何もしない。
  const handleContextMenu = useCallback(
    (objectId: string) => (event: React.MouseEvent<HTMLElement>) => {
      if (readOnly) return;
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      event.preventDefault();
      event.stopPropagation();
      const currentSelected = useCanvasStore.getState().selectedObjectIds;
      if (!currentSelected.includes(objectId)) {
        setSelectedObjectIds([objectId]);
      }
      openContextMenu({ objectId, clientX: event.clientX, clientY: event.clientY });
    },
    [openContextMenu, readOnly, setSelectedObjectIds]
  );

  // isolation: isolate で子要素の z-index が外側へ漏れないようにする。
  // これがないと各オブジェクトの zIndex が canvas-pen-layer など兄弟要素と競合し、
  // ペン描画中のリアルタイムプレビューが既存図形の下に潜り込む不具合が起きる。
  return (
    <div
      data-testid="canvas-objects-layer"
      style={{ position: "absolute", inset: 0, isolation: "isolate" }}
    >
      {visible.map((obj) => (
        <ContextMenuTrigger
          key={obj.id}
          objectId={obj.id}
          onContextMenu={handleContextMenu(obj.id)}
        >
          {renderObject(obj)}
        </ContextMenuTrigger>
      ))}
    </div>
  );
}

function renderObject(
  obj: ReturnType<typeof useCanvasDocStore.getState>["objects"][string]
): ReactNode {
  switch (obj.type) {
    case "sticky":
      return <StickyObject object={obj} />;
    case "section":
      return <SectionObject object={obj} />;
    case "shape":
      return <ShapeObject object={obj} />;
    case "table":
      return <TableObject object={obj} />;
    case "text":
      return <TextObject object={obj} />;
    case "image":
      return <ImageObject object={obj} />;
    case "video":
      return <VideoObject object={obj} />;
    case "link-card":
      return <LinkCardObject object={obj} />;
    case "pen-stroke":
      return <PenStrokeObject object={obj} />;
    case "stamp":
      return <StampObject object={obj} />;
    case "connector":
      return <ConnectorObject object={obj} />;
    default:
      return null;
  }
}

// 右クリックを受け止めるための薄いラッパ。display:contents で本体のレイアウトに影響を与えない。
function ContextMenuTrigger({
  children,
  objectId: _objectId,
  onContextMenu,
}: {
  readonly children: ReactNode;
  readonly objectId: string;
  readonly onContextMenu: (event: React.MouseEvent<HTMLElement>) => void;
}): JSX.Element {
  return (
    <div style={{ display: "contents" }} onContextMenuCapture={onContextMenu}>
      {children}
    </div>
  );
}
